import React, { useMemo } from 'react';
import { Hero as HeroType, ImageOrigin } from '@honeycomb/data';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Button,
    ButtonColor,
    ColorOption,
    Stacker,
    Styler,
    TypographyBody,
    TypographyHero,
    TypographyPrice,
    useBreakpoints,
} from '@honeycomb/ui-core';
import {
    augmentImageUrl,
    HeroContent,
    HeroGradient,
    HeroOverlayImage,
    HeroSearch,
    ImageFormat,
    LandingPageSearch,
    mapCMSBackgroundColorToThemeColor,
} from '@honeycomb/ui';
import { Hero } from 'components/Hero/Hero';
import { MIN_HERO_HEIGHT_L, MIN_HERO_HEIGHT_XS, MIN_HERO_HEIGHT_XXL } from 'components/Hero';

function isLightBackground(backgroundColor: ColorOption) {
    return ['common.white', 'background.offset'].includes(backgroundColor);
}

export type HeroProps = {
    heroData: HeroType;
};

export function LandingPageHero(props: HeroProps): React.JSX.Element {
    const {
        heroData: {
            __typename,
            backgroundColor2: backgroundColorCMS,
            breadcrumbs,
            buttonHref,
            buttonLabel,
            gradient,
            heading,
            hideMobileImage: hideMobileImageProp,
            imageOrigin,
            imageUrl,
            mobileHeroImage,
            searchUnit,
            subheading,
            teaserPrice,
            teaserPriceSubText,
            overlayImage,
            searchTabs,
        },
    } = props;

    const imageSetData = useMemo(() => {
        const type = 'custom';
        const format = imageOrigin === ImageOrigin.Facade || imageUrl.includes('media') ? ImageFormat.Webp : undefined;

        return {
            xxl: augmentImageUrl(
                imageOrigin,
                imageUrl,
                type,
                { breakpoint: 'l', format },
                {
                    width: 1348,
                    height: MIN_HERO_HEIGHT_XXL,
                }
            ),
            l: augmentImageUrl(
                imageOrigin,
                imageUrl,
                type,
                { breakpoint: 'l', format },
                {
                    width: 1240,
                    height: MIN_HERO_HEIGHT_L,
                }
            ),
            m: augmentImageUrl(
                imageOrigin,
                imageUrl,
                type,
                { breakpoint: 'm', format },
                { width: 1024, height: MIN_HERO_HEIGHT_XS }
            ),
            s: augmentImageUrl(
                imageOrigin,
                imageUrl,
                type,
                { breakpoint: 's', format },
                {
                    width: 768,
                    height: MIN_HERO_HEIGHT_XS,
                }
            ),
            xs: augmentImageUrl(
                imageOrigin,
                mobileHeroImage || imageUrl,
                type,
                { breakpoint: 'xs', format },
                {
                    width: 540,
                    height: MIN_HERO_HEIGHT_XS,
                }
            ),
        };
    }, [imageOrigin, imageUrl, mobileHeroImage]);

    const { mediumUp } = useBreakpoints();
    const hideMobileImage = hideMobileImageProp ?? false;
    const hideImage = !mediumUp && hideMobileImage;

    const backgroundColor =
        (backgroundColorCMS && mapCMSBackgroundColorToThemeColor(backgroundColorCMS)) || 'primary.base';
    const lightBackground = hideImage && isLightBackground(backgroundColor);
    const contentColor = lightBackground ? 'text.body' : 'text.inverse';
    const buttonColor: ButtonColor = lightBackground ? 'primary' : 'white';

    return (
        <Hero
            backgroundColor={backgroundColor}
            imageSet={imageSetData}
            ImageProps={{ disableLazy: true, fetchpriority: 'high' }}
            hideMobileImage={hideMobileImage ?? false}
            gradient={(gradient?.toLocaleLowerCase() || 'none') as HeroGradient}
        >
            <HeroContent c={contentColor}>
                {/* TEXT CONTENT */}
                <Stacker spacing={2}>
                    {heading && (
                        <TypographyHero component="h1" size="3xl">
                            {heading}
                        </TypographyHero>
                    )}
                    {subheading && (
                        <TypographyBody component="h2" size="2xl">
                            {subheading}
                        </TypographyBody>
                    )}
                    {teaserPrice && teaserPriceSubText && (
                        <Styler data-id={`section-${__typename}-root-component`}>
                            <TypographyBody size="m">{teaserPriceSubText}</TypographyBody>
                            <TypographyPrice component="h2" size="xl" color="inherit">
                                {teaserPrice.formatted}
                            </TypographyPrice>
                        </Styler>
                    )}
                </Stacker>

                {/* BREADCRUMBS (these scroll for small devices) */}
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <Styler mt={{ xs: 5, l: 6, xxl: 12 }} data-id={`section-${__typename}-breadcrumbs-component`}>
                        <Breadcrumb
                            color={contentColor}
                            overflowGradientColor="transparent"
                            disableScrolling={{ xs: false, l: true }}
                            contained={{ xs: true, l: false }}
                        >
                            {breadcrumbs.map((item, index) => (
                                <BreadcrumbItem key={`Breadcrumb-${item?.url}`}>
                                    <BreadcrumbLink
                                        href={item?.url}
                                        active={index === breadcrumbs.length - 1}
                                        size={{ xs: 'xs', m: 'm' }}
                                    >
                                        {item?.text}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                            ))}
                        </Breadcrumb>
                    </Styler>
                )}

                {/* CTA BUTTON LINK */}
                {buttonLabel && buttonHref ? (
                    <Styler mt={5}>
                        <Button color={buttonColor} variant="outlined" size={{ xs: 'm', l: 'l' }} href={buttonHref}>
                            {buttonLabel}
                        </Button>
                    </Styler>
                ) : null}
            </HeroContent>

            {/* SEARCH UNIT */}
            {searchUnit && (
                <HeroSearch>
                    <LandingPageSearch
                        component={searchUnit}
                        key="SearchUnit"
                        paperProps={{ radius: { xs: 'none', l: 'm' }, shadow: { xs: undefined, l: 'l' } }}
                        searchTabs={searchTabs ?? []}
                    />
                </HeroSearch>
            )}

            {/* Overlaid promotional image */}
            {overlayImage && (
                <HeroOverlayImage
                    w={overlayImage.width}
                    h={overlayImage.height}
                    src={overlayImage.src}
                    src2x={overlayImage.src2x ?? undefined}
                    linkUrl={overlayImage.linkUrl ?? undefined}
                    openInNewTab={overlayImage.openInNewTab ?? false}
                    position={overlayImage.position}
                />
            )}
        </Hero>
    );
}
